import type { Preset, PresetColumn } from "./types";
import { SOURCE_FIELDS, VALID_FIELDS } from "./source-fields";

export { SOURCE_FIELDS, VALID_FIELDS, groupedSourceFields, defaultHeaderFor } from "./source-fields";

export const DEFAULT_PRESET_ID = "__default__";

// Reproduces the 8-column spec from docs/plan.md, using raw API field
// keys (so the same paths the editor exposes drive the default).
export const DEFAULT_PRESET: Preset = {
  id: DEFAULT_PRESET_ID,
  name: "Default",
  description: "Standard 8-column export defined in docs/plan.md.",
  columns: [
    { field: "cardLink", header: "Card Link" },
    { field: "itemName", header: "Name" },
    { field: "gradingID", header: "Cert Number" },
    { field: "gradingCompany", header: "Grader" },
    { field: "grade", header: "Grade" },
    { field: "insuredValue", header: "Insured" },
    { field: "nftAddress", header: "TokenID" },
    { field: "comp", header: "Comp" },
  ],
};

// Bumped from v1 because field keys changed (raw API names instead of
// curated aliases). Older saved presets reference fields that no longer
// exist, so we start fresh under a new key.
const STORAGE_KEY = "cc-wallet-dumper:presets:v2";

export function isBuiltInPreset(p: Preset): boolean {
  return p.id === DEFAULT_PRESET_ID;
}

export function newPresetId(): string {
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function makeBlankPreset(): Preset {
  return {
    id: newPresetId(),
    name: "Untitled preset",
    description: "",
    columns: DEFAULT_PRESET.columns.map((c) => ({ ...c })),
  };
}

export function duplicatePreset(p: Preset): Preset {
  return {
    id: newPresetId(),
    name: `${p.name} (copy)`,
    description: p.description,
    columns: p.columns.map((c) => ({ ...c })),
  };
}

function isValidColumn(c: unknown): c is PresetColumn {
  if (!c || typeof c !== "object") return false;
  const o = c as PresetColumn;
  if (typeof o.field !== "string") return false;
  if (typeof o.header !== "string") return false;
  if (!VALID_FIELDS.has(o.field)) return false;
  if (o.value !== undefined && typeof o.value !== "string") return false;
  return true;
}

export function isValidPreset(p: unknown): p is Preset {
  return (
    !!p &&
    typeof p === "object" &&
    typeof (p as Preset).id === "string" &&
    typeof (p as Preset).name === "string" &&
    typeof (p as Preset).description === "string" &&
    Array.isArray((p as Preset).columns) &&
    (p as Preset).columns.every(isValidColumn)
  );
}

export function loadStoredPresets(): Preset[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPreset).filter((p) => p.id !== DEFAULT_PRESET_ID);
  } catch {
    return [];
  }
}

export function saveStoredPresets(presets: Preset[]): void {
  if (typeof window === "undefined") return;
  const toStore = presets.filter((p) => p.id !== DEFAULT_PRESET_ID);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
}

export function presetToJson(p: Preset): string {
  const exported = { ...p, id: p.id === DEFAULT_PRESET_ID ? newPresetId() : p.id };
  return JSON.stringify(exported, null, 2);
}

export function presetsToJson(ps: Preset[]): string {
  return JSON.stringify(
    ps.map((p) => ({ ...p, id: p.id === DEFAULT_PRESET_ID ? newPresetId() : p.id })),
    null,
    2,
  );
}

export function parsePresetImport(raw: string): Preset[] {
  const parsed = JSON.parse(raw);
  const candidates: unknown[] = Array.isArray(parsed) ? parsed : [parsed];
  const valid = candidates.filter(isValidPreset);
  if (valid.length === 0) {
    throw new Error("No valid preset found in import");
  }
  return valid.map((p) => ({
    ...p,
    id: p.id === DEFAULT_PRESET_ID ? newPresetId() : p.id,
  }));
}
